import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { FoldersType, Keys } from "../pages/DashboardPage/acadia/type";
import _ from "lodash";
import { getLocalItem, setLocalItem } from "src/utils/localStorage";
import { CheckBoxListItem, FilterNode } from "src/types/main";
import { initFolderState } from "src/layouts/DashboardLayout";

interface FoldersContextType {
  folders: FoldersType;
  clearFolders: () => void;
  isFoldersEmpty: () => boolean;
  openFolder: (key: string, value: FilterNode) => void;
  closeFolder: (key: string) => void;
  changeFolder: (key: string) => void;
  updateFolders: (folder: FoldersType) => void;
  fromFilterToFolders: (filter: Record<Keys, CheckBoxListItem[]>) => void;
}

const FoldersContext = createContext<FoldersContextType | null>(null);

export const useFoldersContext = (): FoldersContextType => {
  const context = useContext(FoldersContext);

  if (!context) {
    throw new Error("useFoldersContext должен использоваться внутри FoldersProvider");
  }

  return context;
};

interface FoldersProviderProps {
  initial: FoldersType;
  children: ReactNode;
}

export const FoldersProvider: React.FC<FoldersProviderProps> = ({ initial, children }) => {
  const folderFilter: FoldersType | null = getLocalItem<FoldersType>("folders_object");

  const [folders, setFolders] = useState<FoldersType>(folderFilter ?? initial);

  const clearFolders = () => {
    setFolders(initial);
  };

  function openFolder(key: string, value: FilterNode) {
    const newObj = { ...folders, [key]: value };

    const keysToUpdate = Object.keys(newObj).slice(Object.keys(newObj).indexOf(key) + 1);

    keysToUpdate.forEach((key) => {
      if (typeof key === "string") {
        newObj[key as keyof FoldersType] = null;
      }
    });

    setFolders(newObj);
  }

  function closeFolder(key: string) {
    const tempFolders = _.cloneDeep(folders);
    const keysToUpdate = Object.keys(tempFolders).slice(Object.keys(tempFolders).indexOf(key) + 0);

    keysToUpdate.forEach((key) => {
      if (typeof key === "string") {
        tempFolders[key as keyof FoldersType] = null;
      }
    });

    setFolders(tempFolders);
  }

  function isFoldersEmpty() {
    const hasNonEmptyValues = _.values(folders).some((value) => !_.isEmpty(value));

    return hasNonEmptyValues;
  }

  function updateFolders(folder: FoldersType) {
    const newObj = { ...folders, ...folder };

    setFolders(newObj);
  }

  // изменяет все папки от ключа по иерархии
  function changeFolder(key: string) {
    const tempFolders = _.cloneDeep(folders);
    const keys = Object.keys(tempFolders) as (keyof typeof tempFolders)[];
    const keyIndex = keys.indexOf(key as keyof typeof tempFolders);

    if (keyIndex !== -1) {
      // Устанавливаем пустые строки для всех ключей после выбранного
      keys.slice(keyIndex + 1).forEach((k) => {
        tempFolders[k as keyof typeof tempFolders] = null;
      });

      setFolders(tempFolders);
    }
  }

  function fromFilterToFolders(filters: Record<Keys, CheckBoxListItem[]>) {
    const orderKeys: Keys[] = ["collections", "departments", "sectors", "processes", "types"];

    const baseFilters = _.fromPairs(_.map(orderKeys, (key) => [key, filters[key]]));

    const initialFolders = Object.keys(baseFilters).reduce((acc, currentKey) => {
      const filterItem = baseFilters[currentKey as Keys];

      if (filterItem.length > 0) {
        acc[currentKey as keyof FoldersType] = { ...filterItem[0], key: currentKey } as FilterNode;
      }

      return acc;
    }, _.cloneDeep(initFolderState) as FoldersType);

    setFolders(initialFolders);
  }

  useEffect(() => {
    setLocalItem("folders_object", folders);
  }, [JSON.stringify(folders)]);

  const contextValue: FoldersContextType = {
    folders,
    isFoldersEmpty,
    openFolder,
    clearFolders,
    closeFolder,
    changeFolder,
    updateFolders,
    fromFilterToFolders,
  };

  return <FoldersContext.Provider value={contextValue}>{children}</FoldersContext.Provider>;
};
